// loomraga-tryon.js — <lr-tryon> "See it on you" private preview flow.
// No external libraries. Privacy-first: the customer's photo is held only in memory, never
// written to storage, and (in on-device mode) never transmitted. Everything is revoked/cleared
// on close, discard, or "try another". See TRY-ON-PRIVACY.md.
// The generation adapter is loaded on demand from its asset URL (passed via data-generate-src)
// so we don't depend on Horizon's import map.

const SESSION_DISMISS = 'lr-tryon-dismissed';
const LS_MEASURE = 'lr-tryon-measurements'; // measurements only, on-device, opt-in. NEVER the photo.

class LoomragaTryOn extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-lr-dialog]');
    this.panel = this.querySelector('[data-lr-panel]');
    this.promptEl = this.querySelector('[data-lr-prompt]');
    this.mode = this.dataset.mode || 'on-device';
    this.endpoint = this.dataset.endpoint || '';
    this.loggedIn = this.dataset.loggedIn === 'true';
    this.customerName = this.dataset.customerName || '';
    this.loginUrl = this.dataset.loginUrl || '/account/login';
    this.privacyUrl = this.dataset.privacyUrl || '';
    this.look = this._json('[data-lr-look]') || {};
    this.copy = this._json('[data-lr-copy]') || {};

    this.state = this._freshState();

    // Soft prompt (unless dismissed this session)
    if (this.promptEl && sessionStorage.getItem(SESSION_DISMISS) !== '1') this.promptEl.hidden = false;

    this.addEventListener('click', this._onClick);
    this.dialog?.addEventListener('close', () => this._cleanup());
    this.dialog?.addEventListener('cancel', () => this._cleanup());
  }

  disconnectedCallback() { this._cleanup(); this.removeEventListener('click', this._onClick); }

  _freshState() {
    const saved = this._loadMeasurements();
    return { step: 'intro', photoBlob: null, photoUrl: null, stream: null,
      measurements: saved || { units: 'metric', height: '', weight: '', bodyType: '', size: '' },
      remember: !!saved, consent: false, result: null, busy: false, error: '' };
  }

  _json(sel) { try { return JSON.parse(this.querySelector(sel)?.textContent || 'null'); } catch { return null; } }
  _loadMeasurements() { try { return JSON.parse(localStorage.getItem(LS_MEASURE) || 'null'); } catch { return null; } }

  // ---- events -------------------------------------------------------------
  _onClick = (e) => {
    const dismiss = e.target.closest('[data-lr-dismiss]');
    const open = e.target.closest('[data-lr-open]');
    const action = e.target.closest('[data-action]');
    if (dismiss) { this.promptEl.hidden = true; sessionStorage.setItem(SESSION_DISMISS, '1'); return; }
    if (open) { this._open(); return; }
    if (action) { e.preventDefault(); this._action(action.dataset.action, action); }
  };

  _open() {
    this.state = this._freshState();
    this.state.step = 'intro';
    this._render();
    if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
    else this.dialog.setAttribute('open', '');
  }
  _close() { this.dialog.close?.(); this.dialog.removeAttribute('open'); this._cleanup(); }

  async _action(name, el) {
    const s = this.state;
    switch (name) {
      case 'close': return this._close();
      case 'to-photo': s.step = 'photo'; return this._render();
      case 'to-measure': return this._goMeasure();
      case 'to-consent': s.step = 'consent'; return this._render();
      case 'back-photo': this._stopCamera(); s.step = 'photo'; return this._render();
      case 'back-measure': s.step = 'measure'; return this._render();
      case 'pick-file': return this.panel.querySelector('#lr-file')?.click();
      case 'use-camera': return this._startCamera();
      case 'shoot': return this._shoot();
      case 'retake': this._clearPhoto(); s.step = 'photo'; return this._render();
      case 'generate': return this._generate();
      case 'try-another': this._clearPhoto(); this._clearResult(); s.step = 'photo'; return this._render();
      case 'download': return this._download();
      case 'discard': this._clearResult(); return this._close();
    }
  }

  _goMeasure() {
    if (!this.state.photoBlob) { this.state.error = 'Please add a photo first.'; return this._render(); }
    this.state.error = ''; this.state.step = 'measure'; this._render();
  }

  // ---- photo --------------------------------------------------------------
  _onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { this.state.error = 'Please choose an image file.'; return this._render(); }
    if (file.size > 12 * 1024 * 1024) { this.state.error = 'Please choose an image under 12 MB.'; return this._render(); }
    this._setPhoto(file);
  };

  async _startCamera() {
    try {
      this._stopCamera();
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: 720 }, audio: false });
      this.state.stream = stream; this.state.step = 'camera'; this._render();
      const v = this.panel.querySelector('#lr-video');
      if (v) { v.srcObject = stream; await v.play().catch(() => {}); }
    } catch { this.state.error = 'Camera unavailable. You can upload a photo instead.'; this.state.step = 'photo'; this._render(); }
  }
  _stopCamera() { this.state.stream?.getTracks().forEach((t) => t.stop()); this.state.stream = null; }

  async _shoot() {
    const v = this.panel.querySelector('#lr-video');
    if (!v) return;
    const c = document.createElement('canvas');
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext('2d').drawImage(v, 0, 0);
    const blob = await new Promise((r) => c.toBlob(r, 'image/jpeg', 0.92));
    this._stopCamera();
    this._setPhoto(blob);
  }

  _setPhoto(blob) {
    this._clearPhoto();
    this.state.photoBlob = blob;
    this.state.photoUrl = URL.createObjectURL(blob);
    this.state.error = ''; this.state.step = 'photo';
    this._render();
  }
  _clearPhoto() {
    if (this.state.photoUrl) URL.revokeObjectURL(this.state.photoUrl);
    this.state.photoBlob = null; this.state.photoUrl = null;
  }

  // ---- measurements -------------------------------------------------------
  _readMeasure() {
    const q = (id) => this.panel.querySelector(id)?.value?.trim() || '';
    this.state.measurements = {
      units: this.panel.querySelector('[data-unit][aria-pressed="true"]')?.dataset.unit || 'metric',
      height: q('#lr-h'), weight: q('#lr-w'), bodyType: q('#lr-body'), size: q('#lr-size'),
    };
    this.state.remember = this.panel.querySelector('#lr-remember')?.checked || false;
    if (this.state.remember) { try { localStorage.setItem(LS_MEASURE, JSON.stringify(this.state.measurements)); } catch {} }
    else { try { localStorage.removeItem(LS_MEASURE); } catch {} }
  }

  // ---- generate -----------------------------------------------------------
  async _generate() {
    const s = this.state;
    if (!s.consent) return;
    s.busy = true; s.error = ''; s.step = 'generating'; this._render();
    try {
      if (!this._gen) this._gen = (await import(this.dataset.generateSrc)).generate;
      const result = await this._gen({ photoBlob: s.photoBlob, measurements: s.measurements, look: this.look, mode: this.mode, endpoint: this.endpoint });
      s.result = result;
      // On-device: the photo has served its purpose — drop it now. Endpoint: already transmitted once; drop too.
      this._clearPhoto();
      s.step = 'result';
    } catch (err) {
      s.error = err.message || 'Could not create the preview.'; s.step = 'consent';
    } finally { s.busy = false; this._render(); }
  }

  _download() {
    if (!this.state.result) return;
    const a = document.createElement('a');
    a.href = this.state.result.url; a.download = 'loomraga-look-preview.png';
    document.body.appendChild(a); a.click(); a.remove();
  }

  // ---- privacy cleanup ----------------------------------------------------
  _clearResult() { if (this.state.result?.url) URL.revokeObjectURL(this.state.result.url); this.state.result = null; }
  _cleanup() { this._stopCamera(); this._clearPhoto(); this._clearResult(); }

  // ---- render -------------------------------------------------------------
  _render() {
    this.panel.innerHTML = this._view();
    const file = this.panel.querySelector('#lr-file');
    if (file) file.addEventListener('change', this._onFile);
    const consent = this.panel.querySelector('#lr-consent');
    if (consent) consent.addEventListener('change', (e) => { this.state.consent = e.target.checked; this.panel.querySelector('[data-action="generate"]').disabled = !e.target.checked; });
    this.panel.querySelectorAll('[data-unit]').forEach((b) => b.addEventListener('click', () => {
      this.panel.querySelectorAll('[data-unit]').forEach((x) => x.setAttribute('aria-pressed', x === b ? 'true' : 'false'));
    }));
    const first = this.panel.querySelector('[data-autofocus]') || this.panel.querySelector('button, input, a');
    first?.focus();
  }

  _shell(title, bodyHtml, footHtml) {
    return `<div class="lr-tryon__head">
        <div><p class="lr-tryon__eyebrow">See it on you</p><h2 class="lr-tryon__title" id="lr-tryon-heading">${title}</h2></div>
        <button type="button" class="lr-tryon__close" data-action="close" aria-label="Close">&times;</button>
      </div>
      <div class="lr-tryon__body">${this.state.error ? `<p class="lr-tryon__p" role="alert" style="color:#8a1c1c">${this._esc(this.state.error)}</p>` : ''}${bodyHtml}</div>
      <div class="lr-tryon__foot">${footHtml}</div>`;
  }

  _view() {
    const s = this.state, c = this.copy;
    switch (s.step) {
      case 'intro': return this._shell('A private preview of this look', `
        <p class="lr-tryon__p">${this._esc(c.prompt_text || 'See how this look suits you.')}</p>
        <div class="lr-tryon__badges">
          <div class="lr-tryon__badge"><div class="lr-tryon__badge-icon">🔒</div><span class="lr-tryon__badge-label">Not stored</span></div>
          <div class="lr-tryon__badge"><div class="lr-tryon__badge-icon">📱</div><span class="lr-tryon__badge-label">${this.mode === 'on-device' ? 'Stays on device' : 'Sent once, then deleted'}</span></div>
          <div class="lr-tryon__badge"><div class="lr-tryon__badge-icon">🗑️</div><span class="lr-tryon__badge-label">Discarded after</span></div>
        </div>
        <p class="lr-tryon__p lr-tryon__muted">${this._esc(c.promise || '')}${this.mode === 'endpoint' ? ' ' + this._esc(c.endpoint_notice || '') : ''}</p>
        ${!this.loggedIn ? `<p class="lr-tryon__p lr-tryon__muted">Optional: <a href="${this._esc(this.loginUrl)}">log in</a> to remember your measurements next time. You can also continue as a guest.</p>` : `<p class="lr-tryon__p lr-tryon__muted">Hi ${this._esc(this.customerName) || 'there'} — welcome back.</p>`}
      `, `<button class="lr-tryon__btn lr-tryon__btn--ghost" data-action="close">Maybe later</button>
          <button class="lr-tryon__btn lr-tryon__btn--primary" data-action="to-photo" data-autofocus>Continue</button>`);

      case 'photo': return this._shell('Add your photo', `
        <div class="lr-tryon__photo">
          <div class="lr-tryon__dropzone">
            ${s.photoUrl ? `<img src="${s.photoUrl}" alt="Your uploaded photo">` : `<div class="lr-tryon__dropzone-hint">A clear, front-facing, full-length photo in good light gives the best preview.<br>Plain background works best.</div>`}
          </div>
          <input type="file" id="lr-file" accept="image/*" hidden>
          <div class="lr-tryon__photo-actions">
            <button class="lr-tryon__btn" data-action="pick-file">${s.photoUrl ? 'Choose another' : 'Upload a photo'}</button>
            <button class="lr-tryon__btn" data-action="use-camera">Use camera</button>
          </div>
          <p class="lr-tryon__p lr-tryon__muted" style="text-align:center">${this.mode === 'on-device' ? 'Your photo stays on your device — it is not uploaded here.' : 'Your photo is only sent when you tap Generate, then deleted.'}</p>
        </div>
      `, `<button class="lr-tryon__btn lr-tryon__btn--ghost" data-action="close">Cancel</button>
          <button class="lr-tryon__btn lr-tryon__btn--primary" data-action="to-measure" ${s.photoBlob ? '' : 'disabled'}>Next</button>`);

      case 'camera': return this._shell('Take a photo', `
        <div class="lr-tryon__photo">
          <div class="lr-tryon__dropzone"><video id="lr-video" playsinline muted></video></div>
          <div class="lr-tryon__photo-actions">
            <button class="lr-tryon__btn lr-tryon__btn--primary" data-action="shoot" data-autofocus>Capture</button>
            <button class="lr-tryon__btn" data-action="back-photo">Back</button>
          </div>
        </div>
      `, `<button class="lr-tryon__btn lr-tryon__btn--ghost" data-action="back-photo">Cancel</button>`);

      case 'measure': {
        const m = s.measurements;
        return this._shell('A few details for a better fit', `
          <div class="lr-tryon__field"><label>Units</label>
            <div class="lr-tryon__unit-toggle">
              <button type="button" data-unit="metric" aria-pressed="${m.units !== 'imperial'}">Metric</button>
              <button type="button" data-unit="imperial" aria-pressed="${m.units === 'imperial'}">Imperial</button>
            </div>
          </div>
          <div class="lr-tryon__row">
            <div class="lr-tryon__field"><label for="lr-h">Height</label><input type="text" id="lr-h" inputmode="decimal" value="${this._esc(m.height)}" placeholder="e.g. 165"></div>
            <div class="lr-tryon__field"><label for="lr-w">Weight</label><input type="text" id="lr-w" inputmode="decimal" value="${this._esc(m.weight)}" placeholder="e.g. 60"></div>
          </div>
          <div class="lr-tryon__row">
            <div class="lr-tryon__field"><label for="lr-body">Body type</label>
              <select id="lr-body">${['', 'Petite', 'Average', 'Athletic', 'Curvy', 'Plus', 'Tall'].map((o) => `<option ${m.bodyType === o ? 'selected' : ''} value="${o}">${o || 'Prefer not to say'}</option>`).join('')}</select>
            </div>
            <div class="lr-tryon__field"><label for="lr-size">Usual size (optional)</label><input type="text" id="lr-size" value="${this._esc(m.size)}" placeholder="e.g. M"></div>
          </div>
          <p class="lr-tryon__p lr-tryon__muted">These help render proportions. They are optional.</p>
          <label class="lr-tryon__consent"><input type="checkbox" id="lr-remember" ${s.remember ? 'checked' : ''}><span>Remember my measurements on this device only (never my photo). Stored locally in this browser; clear anytime.</span></label>
        `, `<button class="lr-tryon__btn" data-action="back-photo">Back</button>
            <button class="lr-tryon__btn lr-tryon__btn--primary" data-action="save-measure" data-autofocus>Next</button>`);
      }

      case 'consent': return this._shell('Consent & privacy', `
        <p class="lr-tryon__p">Before we create your preview, please confirm you're happy for us to use your photo for this.</p>
        <label class="lr-tryon__consent">
          <input type="checkbox" id="lr-consent" ${s.consent ? 'checked' : ''}>
          <span>${this._esc(c.consent || 'I consent to my photo being used to generate this preview only. It is not stored or used for anything else.')}</span>
        </label>
        <p class="lr-tryon__disclaimer">${this._esc(c.disclaimer || '')}${this.mode === 'endpoint' ? ' ' + this._esc(c.endpoint_notice || '') : ''}${this.privacyUrl ? ` <a href="${this._esc(this.privacyUrl)}">Read our Privacy Notice</a>.` : ''}</p>
      `, `<button class="lr-tryon__btn" data-action="back-measure">Back</button>
          <button class="lr-tryon__btn lr-tryon__btn--primary" data-action="generate" ${s.consent ? '' : 'disabled'}>Generate my preview</button>`);

      case 'generating': return this._shell('Creating your preview…', `
        <div class="lr-tryon__spinner" role="status" aria-label="Generating"></div>
        <p class="lr-tryon__p lr-tryon__muted" style="text-align:center">${this.mode === 'on-device' ? 'Composing on your device — nothing is being uploaded.' : 'Processing securely — your photo will be deleted right after.'}</p>
      `, '');

      case 'result': return this._shell('Your look preview', `
        ${s.result ? `<img class="lr-tryon__result-img" src="${s.result.url}" alt="Your stylised look preview">` : ''}
        <div class="lr-tryon__deleted">✓ Your photo has been discarded and was not stored.</div>
        <p class="lr-tryon__p lr-tryon__muted" style="margin-top:0.8rem">This is a stylised preview for personal use. Save it to your device if you'd like to keep it — we don't keep a copy.</p>
      `, `<button class="lr-tryon__btn" data-action="try-another">Try another photo</button>
          <button class="lr-tryon__btn" data-action="download">Save image</button>
          <button class="lr-tryon__btn lr-tryon__btn--primary" data-action="discard">Done</button>`);

      default: return this._shell('See it on you', '<p class="lr-tryon__p">Something went wrong.</p>', `<button class="lr-tryon__btn" data-action="close">Close</button>`);
    }
  }

  _esc(v) { return String(v ?? '').replace(/[&<>"']/g, (m) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[m])); }
}

// Save-measure needs to read the DOM before advancing.
LoomragaTryOn.prototype._action = (function (orig) {
  return function (name, el) {
    if (name === 'save-measure') { this._readMeasure(); this.state.step = 'consent'; return this._render(); }
    return orig.call(this, name, el);
  };
})(LoomragaTryOn.prototype._action);

if (!customElements.get('lr-tryon')) customElements.define('lr-tryon', LoomragaTryOn);
